import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { useAuthStore } from './stores/authStore';
import useThemeStore from './stores/themeStore';
import useConfigStore from './stores/configStore';
import Layout from './components/Layout';
import Login from './pages/Login';
import Home from './pages/Home';
import Files from './pages/Files';
import Items from './pages/Items';
import Finance from './pages/Finance';
import Time from './pages/Time';
import Entertainment from './pages/Entertainment';
import Customers from './pages/Customers';
import Body from './pages/Body';
import Contacts from './pages/Contacts';
import Study from './pages/Study';
import Projects from './pages/Projects';
import Health from './pages/Health';
import Goals from './pages/Goals';
import Habits from './pages/Habits';
import Risk from './pages/Risk';
import Family from './pages/Family';
import Creative from './pages/Creative';
import Environment from './pages/Environment';
import Energy from './pages/Energy';
import Decisions from './pages/Decisions';
import Emotions from './pages/Emotions';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import CardSales from './pages/CardSales';
import QRPayment from './pages/QRPayment';
import DebugLogin from './pages/DebugLogin';
import MyOrders from './pages/MyOrders';
import Referral from './pages/Referral';
// import Activation from './pages/Activation';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import DeleteAccount from './pages/DeleteAccount';
import Billing from './pages/Billing';
import ProductShowcase from './pages/ProductShowcase';
import About from './pages/About';
import Contact from './pages/Contact';
import WeChatCallback from './pages/WeChatCallback';
import TsAgentPortal from './pages/TsAgentPortal';
import Messages from './pages/Messages';
import TsDemoPublic from './pages/TsDemoPublic';
import './App.css';

function App() {
  const { user, token, logout, initialize } = useAuthStore();
  const { fetchCurrentTheme } = useThemeStore();
  const { membershipEnabled, fetchConfig } = useConfigStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isInitializing, setIsInitializing] = useState(true);

  const effectiveToken = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
  
  // 全局捕获推荐码并保存到localStorage
  useEffect(() => {
    const referrerCode = searchParams.get('ref');
    if (referrerCode && !token) {
      // 只在未登录时保存推荐码
      localStorage.setItem('referrerCode', referrerCode);
      console.log('已保存推荐码:', referrerCode);
    }
  }, [searchParams, token]);
  
  // 初始化认证状态（优先处理URL参数中的token）
  useEffect(() => {
    const initAuth = async () => {
      // 先检查URL参数中是否有token（APP登录）
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      if (urlToken && !window.hasProcessedLoginParams) {
        console.log('🔑 检测到URL中的token，优先处理:', urlToken.substring(0, 20) + '...');
        localStorage.setItem('token', urlToken);
        window.hasProcessedLoginParams = true;
        // 清除URL参数，避免重复处理
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
      
      // 然后初始化
      await initialize();

      // 只要存在 token，就用后端权威用户信息刷新一次（含 member_level/role/trial_end_date）。
      // 用 getState() 取最新值，避免闭包里的 token/user 是旧值；用 refreshUser 静默刷新，
      // 网络失败不登出。这样 App/网页/小程序冷启动首屏，会员判断即拿到最新等级，
      // 不会出现「本是高级会员却提示升级、要先进『我的』才正常」的问题。
      const hasToken = !!(useAuthStore.getState().token || localStorage.getItem('token'));
      if (hasToken) {
        try {
          await useAuthStore.getState().refreshUser();
        } catch (error) {
          console.error('refreshUser失败:', error);
        }
      }

      setIsInitializing(false);
    };
    
    initAuth();
  }, [initialize, token]);

  // 处理登录信息（从小程序传递）
  useEffect(() => {
    // 防止重复处理
    if (window.hasProcessedLoginParams) {
      console.log('已经处理过登录参数，跳过');
      return;
    }
    
    // 检查URL参数
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userInfoParam = urlParams.get('userInfo');
    const openid = urlParams.get('openid');
    
    console.log('App.js检查URL参数:', { token, userInfoParam, openid });
    
    // 处理登录信息的函数
    const processLogin = async ({ token, userInfo, openid }) => {
      try {
        console.log('开始处理登录信息:', { token, userInfo, openid });
        
        // 使用authStore的微信登录处理方法
        const { handleWeChatLogin } = useAuthStore.getState();
        const result = await handleWeChatLogin({
          token: token,
          user: userInfo
        });
        
        if (result.success) {
          // 保存openid到localStorage，用于支付
          localStorage.setItem('wechat_openid', openid);
          console.log('保存openid到localStorage:', openid);
          
          // 清除URL参数，避免刷新页面时重复处理
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
          message.success('登录状态已同步');
        } else {
          message.error('登录状态同步失败');
        }
      } catch (error) {
        console.error('处理登录信息失败:', error);
        message.error('登录信息处理失败');
      }
    };
    
    // 监听postMessage
    const handleMessage = (event) => {
      console.log('收到postMessage:', event.data);
      
      if (event.data && event.data.type === 'wechat_login') {
        const { openid, token, userInfo } = event.data.data;
        console.log('通过postMessage收到登录信息:', { openid, token, userInfo });
        
        // 标记已处理，防止重复
        window.hasProcessedLoginParams = true;
        
        // 处理登录
        processLogin({ token, userInfo, openid });
      }
    };
    
    window.addEventListener('message', handleMessage);
      
    // 处理URL参数中的登录信息
    if (token && userInfoParam && openid) {
      (async () => {
        try {
          const userInfo = JSON.parse(decodeURIComponent(userInfoParam));
          console.log('从URL参数获取到小程序登录信息:', { token, userInfo, openid });
          
          // 标记已处理，防止重复
          window.hasProcessedLoginParams = true;
          
          // 处理登录
          await processLogin({ token, userInfo, openid });
        } catch (error) {
          console.error('解析小程序登录信息失败:', error);
          message.error('登录信息解析失败');
          document.title = '❌ 登录信息解析失败';
        }
      })();
    }
    // 处理APP登录（只有token，没有userInfo和openid）
    else if (token && !userInfoParam && !openid) {
      (async () => {
        try {
          console.log('从URL参数获取到APP登录token:', token);
          
          // 标记已处理，防止重复
          window.hasProcessedLoginParams = true;
          
          // 直接保存token到localStorage，然后初始化
          localStorage.setItem('token', token);
          
          // 重新初始化认证状态
          const { initialize } = useAuthStore.getState();
          await initialize();
          
          // 验证token并获取用户信息
          const { verifyToken } = useAuthStore.getState();
          await verifyToken();
          
          console.log('APP登录token已保存并初始化');
          message.success('登录成功');
          
          // 清除URL参数
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        } catch (error) {
          console.error('处理APP登录token失败:', error);
          message.error('登录失败');
        }
      })();
    }
    // 处理旧的参数格式（向后兼容）
    else if (token) {
      const userId = urlParams.get('userId');
      const userRole = urlParams.get('userRole');
      
      if (userId) {
        console.log('从URL参数获取到登录信息:', { token, userId, userRole });
        
        // 标记已处理，防止重复
        window.hasProcessedLoginParams = true;
        
        // 将登录信息保存到localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userId);
        if (userRole) {
          localStorage.setItem('userRole', userRole);
        }
        
        // 重新初始化认证状态
        initialize();
        
        // 清除URL参数，避免刷新页面时重复处理
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        message.success('登录状态已同步');
        document.title = '✅ 登录状态已同步';
      }
    }
    
    // 清理事件监听器
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [initialize]);

  // 初始化主题
  useEffect(() => {
    if (user && token) {
      fetchCurrentTheme();
    }
  }, [user, token, fetchCurrentTheme]);

  // 拉取全局站点配置（会员付费开关）
  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // 权限检查函数
  const checkUserPermission = (user) => {
    // 全局「会员付费」关闭时（审核期），所有账号免费使用全部模块
    if (!membershipEnabled) {
      return true;
    }
    // 管理员始终有权限
    if (user?.role === 'admin') {
      return true;
    }

    // 检查是否是付费会员
    const paidMembers = ['VIP会员', '高级会员', '银卡会员', '金卡会员', '钻石会员'];
    if (paidMembers.includes(user?.member_level)) {
      return true;
    }

    // 检查试用期
    if (user?.trial_end_date) {
      const trialEndDate = new Date(user.trial_end_date);
      const now = new Date();
      return now <= trialEndDate;
    }

    return false;
  };

  // 检查用户权限
  const isVipUser = checkUserPermission(user);
  
  // 普通会员只能访问的模块
  const basicModules = ['files', 'items', 'finance', 'time'];
  
  // 检查当前路径是否需要VIP权限
  const checkVipAccess = (path) => {
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      const moduleName = pathSegments[0];
      return basicModules.includes(moduleName);
    }
    return true; // 首页不需要检查
  };

  // 检查token是否有效
  const isValidToken = !!(effectiveToken && effectiveToken.length > 0);

  // 刷新用户信息（拿最新会员等级等）。用 refreshUser 而非 verifyToken：
  // verifyToken 在 /auth/verify 出现瞬时失败时会 logout()，会把刚登录设置的 token 清掉，
  // 导致「密码登录后被弹回登录页、要登录两次才跳转」。refreshUser 失败时不登出。
  useEffect(() => {
    if (isValidToken) {
      useAuthStore.getState().refreshUser();
    }
  }, [isValidToken]);

  // 清除无效的token
  useEffect(() => {
    if (effectiveToken && effectiveToken.length === 0) {
      logout();
    }
  }, [effectiveToken, logout]);

  // PC 端 5 分钟无操作自动退出登录（小程序 / APP / 手机浏览器不启用）
  useEffect(() => {
    if (!isValidToken) return;
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isMiniProgram = typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram';
    const isApp = ua.includes('ManagementMasterApp');
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|HarmonyOS/i.test(ua);
    const isPC = !isMiniProgram && !isApp && !isMobile;
    if (!isPC) return;

    const IDLE_MS = 5 * 60 * 1000; // 5 分钟
    let timer = null;
    const doLogout = () => {
      logout();
      message.warning('您已 5 分钟未操作，为保障账号安全已自动退出登录');
    };
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(doLogout, IDLE_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'wheel', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [isValidToken, logout]);

  // 如果没有登录，显示登录页面，但允许访问首页
  // 等待初始化完成后再检查
  if (isInitializing) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>加载中...</div>;
  }
  
  if (!isValidToken && !['/', '/privacy', '/terms', '/about', '/contact', '/product', '/billing', '/wechat-callback', '/ts-demo'].includes(location.pathname)) {
    return <Login />;
  }

  // 公开的加密演示页：独立全屏，不套用 App 布局与底部导航
  if (location.pathname === '/ts-demo') {
    return (
      <div className="app-container">
        <TsDemoPublic />
      </div>
    );
  }

  // 移除URL拦截，让普通会员可以进入高级模块页面，在页面内显示高级会员专享提示

  // 如果是管理员访问管理页面，不显示底部导航
  if (location.pathname === '/admin') {
    return (
      <div className="app-container">
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/files" element={isValidToken ? <Files /> : <Login />} />
        <Route path="/items" element={isValidToken ? <Items /> : <Login />} />
        <Route path="/finance" element={isValidToken ? <Finance /> : <Login />} />
        <Route path="/time" element={isValidToken ? <Time /> : <Login />} />
        <Route path="/entertainment" element={isValidToken ? <Entertainment /> : <Login />} />
        <Route path="/customers" element={isValidToken ? <Customers /> : <Login />} />
        <Route path="/body" element={isValidToken ? <Body /> : <Login />} />
        <Route path="/contacts" element={isValidToken ? <Contacts /> : <Login />} />
        <Route path="/study" element={isValidToken ? <Study /> : <Login />} />
        <Route path="/projects" element={isValidToken ? <Projects /> : <Login />} />
        <Route path="/health" element={isValidToken ? <Health /> : <Login />} />
        <Route path="/goals" element={isValidToken ? <Goals /> : <Login />} />
        <Route path="/habits" element={isValidToken ? <Habits /> : <Login />} />
        <Route path="/risk" element={isValidToken ? <Risk /> : <Login />} />
        <Route path="/family" element={isValidToken ? <Family /> : <Login />} />
        <Route path="/creative" element={isValidToken ? <Creative /> : <Login />} />
        <Route path="/environment" element={isValidToken ? <Environment /> : <Login />} />
        <Route path="/energy" element={isValidToken ? <Energy /> : <Login />} />
        <Route path="/decisions" element={isValidToken ? <Decisions /> : <Login />} />
        <Route path="/emotions" element={isValidToken ? <Emotions /> : <Login />} />
        <Route path="/profile" element={isValidToken ? <Profile /> : <Login />} />
        <Route path="/messages" element={isValidToken ? <Messages /> : <Login />} />
        <Route path="/referral" element={isValidToken ? <Referral /> : <Login />} />
        <Route path="/card-sales" element={isValidToken ? <CardSales /> : <Login />} />
        <Route path="/qr-payment" element={isValidToken ? <QRPayment /> : <Login />} />
        <Route path="/debug-login" element={<DebugLogin />} />
        <Route path="/my-orders" element={isValidToken ? <MyOrders /> : <Login />} />
               {/* 激活页已下线：支付成功自动开通VIP */}
        <Route path="/ts-agent" element={isValidToken ? <TsAgentPortal /> : <Login />} />
        <Route path="/ts-demo" element={<TsDemoPublic />} />
        <Route path="/wechat-callback" element={<WeChatCallback />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={isValidToken ? <DeleteAccount /> : <Login />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/product" element={<ProductShowcase />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
